// Одна и та же логика используется в Node-генераторе и в браузере.
// 768 — телефоны с DPR 1,75–2 (фото во всю ширину ~335–370 px): без этой ступени они брали 960
export const IMAGE_WIDTHS = [192, 320, 480, 640, 768, 960, 1280, 1600];

/** @param {number} originalWidth @param {readonly number[]} [candidates] */
export const selectWidths = (originalWidth, candidates = IMAGE_WIDTHS) => {
  if (!Number.isInteger(originalWidth) || originalWidth <= 0) return [];
  const valid = candidates.filter((width) => Number.isInteger(width) && width > 0);
  const maximum = Math.min(originalWidth, Math.max(0, ...valid));
  if (!maximum) return [];
  return [...new Set([...valid.filter((width) => width <= maximum), maximum])].sort(
    (a, b) => a - b,
  );
};

/** @param {string} base @param {number} width @param {'avif' | 'webp'} format */
export const variantUrl = (base, width, format) => `${base}-${width}.${format}`;

/**
 * Варианты, которые должен создать генератор, — без повторов по URL: одинаковые фото по разным
 * путям дают один хеш, и два параллельных кодирования писали бы один файл
 * @param {readonly {base: string, widths: readonly number[]}[]} entries
 * @param {readonly ('avif' | 'webp')[]} formats
 */
export const planVariants = (entries, formats) => {
  const seen = new Set();
  const variants = [];
  for (const { base, widths } of entries) {
    for (const width of widths) {
      for (const format of formats) {
        const url = variantUrl(base, width, format);
        if (seen.has(url)) continue;
        seen.add(url);
        variants.push({ base, width, format, url });
      }
    }
  }
  return variants;
};

/**
 * Файлы каталога вариантов, которых нет в текущем наборе: старые хеши после замены фото или
 * смены параметров кодека. Незавершённые `.tmp` здесь не учитываются — их убирают отдельно
 * @param {readonly string[]} fileNames имена файлов в каталоге вариантов
 * @param {readonly string[]} expectedUrls URL текущих вариантов (`/generated-images/…`)
 */
export const findStaleVariants = (fileNames, expectedUrls) => {
  const expected = new Set(expectedUrls.map((url) => url.slice(url.lastIndexOf('/') + 1)));
  return fileNames.filter((name) => !name.endsWith('.tmp') && !expected.has(name)).sort();
};

/**
 * @param {{base: string, widths: readonly number[]} | undefined} entry
 * @param {'avif' | 'webp'} format
 */
export const buildSrcSet = (entry, format) =>
  entry?.widths.length
    ? entry.widths.map((width) => `${variantUrl(entry.base, width, format)} ${width}w`).join(', ')
    : undefined;

/**
 * @param {number} width @param {number} height @param {string} base
 * @param {readonly number[]} widths
 */
export const createManifestEntry = (width, height, base, widths) => {
  if (!Number.isInteger(width) || width <= 0 || !Number.isInteger(height) || height <= 0) {
    throw new Error('Некорректные размеры изображения');
  }
  return {
    width,
    height,
    base,
    widths: [
      ...new Set(widths.filter((value) => Number.isInteger(value) && value > 0 && value <= width)),
    ].sort((a, b) => a - b),
  };
};
